import React, { useState, useEffect } from 'react';
import { Timer, Heart, RefreshCw, ArrowRight, Check } from 'lucide-react';

const CouplesExercises = () => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerComplete, setTimerComplete] = useState(false);

  const exercises = [
    {
      id: 1,
      title: "Co-Regulation Breathing",
      category: "Nervous System Co-Regulation",
      duration: "5-10 minutes",
      description: "Attune to each other's breath and nervous system state through synchronized breathing.",
      roles: {
        initiator: "Guide the breath pace",
        inquirer: "Attune and follow"
      },
      steps: [
        {
          instruction: "Sit comfortably facing each other, close enough to feel a sense of connection but not touching yet.",
          duration: 60,
          role: "both"
        },
        {
          instruction: "Initiator: Begin breathing at your natural pace. Notice your breath without changing it. Let your partner see your chest or belly rise and fall.",
          duration: 120,
          role: "initiator"
        },
        {
          instruction: "Inquirer: Simply observe your partner's breathing pattern. Notice the rhythm, the depth, the quality. Don't match it yet - just witness.",
          duration: 120,
          role: "inquirer"
        },
        {
          instruction: "Inquirer: Now gently begin to synchronize your breath with your partner's. Find their rhythm. Notice what it feels like to breathe together.",
          duration: 180,
          role: "inquirer"
        },
        {
          instruction: "Both: Continue breathing together. Notice what happens in your body and between you. What settles? What shifts?",
          duration: 180,
          role: "both"
        },
        {
          instruction: "Switch roles and repeat. Inquirer becomes Initiator.",
          duration: 0,
          role: "both"
        }
      ]
    },
    {
      id: 2,
      title: "Safe Haven Check-In",
      category: "Attunement Building",
      duration: "10-15 minutes",
      description: "Create a moment of emotional attunement by sharing and receiving without fixing or advising.",
      roles: {
        initiator: "Share what you're carrying",
        inquirer: "Listen and reflect"
      },
      steps: [
        {
          instruction: "Decide who will be Initiator first. Sit in a comfortable position where you can see each other.",
          duration: 30,
          role: "both"
        },
        {
          instruction: "Initiator: Share what you're carrying emotionally right now. This could be stress, joy, worry, or confusion. Speak from your felt sense, not just your thoughts.",
          duration: 180,
          role: "initiator"
        },
        {
          instruction: "Inquirer: Listen with your full attention. Notice your partner's body, tone, and energy. You're here to receive, not to fix or advise.",
          duration: 180,
          role: "inquirer"
        },
        {
          instruction: "Inquirer: Reflect back what you heard and sensed. 'What I'm hearing is...' or 'It seems like you're feeling...'. Check if you've understood.",
          duration: 120,
          role: "inquirer"
        },
        {
          instruction: "Initiator: Respond. Does your partner understand? What else do they need to know?",
          duration: 60,
          role: "initiator"
        },
        {
          instruction: "Take a breath together. Then switch roles and repeat.",
          duration: 30,
          role: "both"
        }
      ]
    },
    {
      id: 3,
      title: "Repair Ritual",
      category: "Repair and Reconnection",
      duration: "10-15 minutes",
      description: "A structured way to reconnect after disconnection, misattunement, or conflict.",
      roles: {
        initiator: "Name the rupture",
        inquirer: "Receive and respond"
      },
      steps: [
        {
          instruction: "Both: Acknowledge that something happened that created distance between you. Take a moment to settle your nervous systems before beginning.",
          duration: 60,
          role: "both"
        },
        {
          instruction: "Initiator: Name what happened from your perspective, focusing on your own experience. 'When [this happened], I felt...' or 'My system went into...'",
          duration: 120,
          role: "initiator"
        },
        {
          instruction: "Inquirer: Listen without defending. Notice what arises in your body. Can you hold space for your partner's experience even if yours was different?",
          duration: 120,
          role: "inquirer"
        },
        {
          instruction: "Inquirer: Acknowledge what you heard. 'I hear that you felt...' You don't have to agree, just acknowledge their experience as real for them.",
          duration: 90,
          role: "inquirer"
        },
        {
          instruction: "Inquirer: Share your own experience of what happened. 'For me, what happened was...' Speak to your own feelings and reactions.",
          duration: 120,
          role: "inquirer"
        },
        {
          instruction: "Initiator: Receive and acknowledge your partner's experience.",
          duration: 90,
          role: "initiator"
        },
        {
          instruction: "Both: What do you each need right now to feel reconnected? Name it and see if you can offer it to each other.",
          duration: 120,
          role: "both"
        }
      ]
    },
    {
      id: 4,
      title: "Curious Inquiry",
      category: "Communication Deepening",
      duration: "8-12 minutes",
      description: "Practice being genuinely curious about your partner's inner world without agenda.",
      roles: {
        initiator: "Choose a topic",
        inquirer: "Ask curious questions"
      },
      steps: [
        {
          instruction: "Initiator: Choose something you've been thinking or feeling about lately - could be a decision, a feeling, a dream, or a memory.",
          duration: 60,
          role: "initiator"
        },
        {
          instruction: "Initiator: Share briefly what you've chosen (30 seconds to 1 minute).",
          duration: 90,
          role: "initiator"
        },
        {
          instruction: "Inquirer: Ask genuine, open questions to help your partner explore further. Not 'why' questions, but 'what' and 'how': 'What does that feel like?' 'How does that land in your body?' 'What's important about that?'",
          duration: 180,
          role: "inquirer"
        },
        {
          instruction: "Initiator: Respond to the questions. Let yourself be curious too. You don't need to have clear answers.",
          duration: 180,
          role: "initiator"
        },
        {
          instruction: "Both: Pause and notice what this way of connecting feels like. What opened up?",
          duration: 60,
          role: "both"
        },
        {
          instruction: "Switch roles and repeat.",
          duration: 0,
          role: "both"
        }
      ]
    },
    {
      id: 5,
      title: "Body-to-Body Connection",
      category: "Nervous System Co-Regulation",
      duration: "5-8 minutes",
      description: "Use gentle physical connection to support nervous system regulation and safety.",
      roles: {
        initiator: "Offer your presence",
        inquirer: "Receive and notice"
      },
      steps: [
        {
          instruction: "Decide on a comfortable physical connection: sitting back-to-back, hand on heart, or simply sitting close with hands held.",
          duration: 60,
          role: "both"
        },
        {
          instruction: "Both: Settle into the connection. Notice the warmth, the weight, the rhythm of your partner's body.",
          duration: 120,
          role: "both"
        },
        {
          instruction: "Inquirer: Notice what happens in your nervous system. Does it settle? Activate? What sensations arise?",
          duration: 120,
          role: "inquirer"
        },
        {
          instruction: "Initiator: Simply be present. Your role is to offer steady, regulated presence. Notice your own system while staying connected.",
          duration: 120,
          role: "initiator"
        },
        {
          instruction: "Both: Stay connected for a few more moments. Let your nervous systems communicate beneath words.",
          duration: 120,
          role: "both"
        },
        {
          instruction: "Gently separate. Share briefly what you noticed. Then switch roles if desired.",
          duration: 60,
          role: "both"
        }
      ]
    }
  ];

  // Timer logic
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            setTimerActive(false);
            setTimerComplete(true);
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (!timerActive && timeLeft !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const startTimer = (duration) => {
    setTimeLeft(duration);
    setTimerActive(true);
    setTimerComplete(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const nextStep = () => {
    // Reset all timer state when moving to next step
    setTimerActive(false);
    setTimerComplete(false);
    setTimeLeft(0);
    if (currentStep < selectedExercise.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const resetExercise = () => {
    setSelectedExercise(null);
    setCurrentStep(0);
    setTimerActive(false);
    setTimeLeft(0);
    setTimerComplete(false);
  };

  // Main menu view
  if (!selectedExercise) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Heart className="w-12 h-12 text-rose-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-800 mb-3">Couples Connection</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Exercises to deepen attunement, regulate together, and strengthen your relational bond.
            </p>
          </div>

          {/* Exercise Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {exercises.map((exercise) => (
              <div
                key={exercise.id}
                className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-rose-100 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-102"
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`exercise-card-${exercise.id}`}
              >
                <div className="mb-4">
                  <span className="text-xs font-medium text-rose-600 bg-rose-50 px-3 py-1 rounded-full">
                    {exercise.category}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{exercise.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{exercise.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Timer className="w-4 h-4" />
                    <span>{exercise.duration}</span>
                  </div>
                  <div className="flex items-center gap-1 text-rose-500 font-medium">
                    <span>Begin</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Privacy Notice */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-400">
              🔒 This tool stores no data. All interactions remain private and local to your device.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Exercise view
  const currentStepData = selectedExercise.steps[currentStep];
  const isLastStep = currentStep === selectedExercise.steps.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={resetExercise}
            className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-2 transition-colors"
            data-testid="back-to-menu-btn"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span>Back to exercises</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-light text-gray-800 mb-2">{selectedExercise.title}</h1>
          <p className="text-gray-600">{selectedExercise.description}</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-600">Step {currentStep + 1} of {selectedExercise.steps.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-rose-400 to-purple-400 h-2 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / selectedExercise.steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-12 border border-rose-100">
          {/* Role Badge */}
          {currentStepData.role !== 'both' && (
            <div className="mb-6">
              {currentStepData.role === 'initiator' ? (
                <span className="inline-flex items-center gap-2 text-sm font-bold text-white bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-3 rounded-full shadow-lg border-2 border-rose-300">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                  🎯 INITIATOR
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm font-bold text-white bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-3 rounded-full shadow-lg border-2 border-purple-300">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                  🎧 INQUIRER
                </span>
              )}
            </div>
          )}

          {/* Instruction */}
          <div className="mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light">
              {currentStepData.instruction}
            </p>
          </div>

          {/* Timer */}
          {currentStepData.duration > 0 && (
            <div className="mb-8">
              {!timerActive && timeLeft === 0 && (
                <button
                  onClick={() => startTimer(currentStepData.duration)}
                  className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-purple-500 hover:from-rose-600 hover:to-purple-600 text-white px-6 py-3 rounded-full shadow-lg transition-all duration-300"
                  data-testid="start-timer-btn"
                >
                  <Timer className="w-5 h-5" />
                  <span>Start Timer ({formatTime(currentStepData.duration)})</span>
                </button>
              )}

              {(timerActive || timeLeft > 0) && (
                <div className="text-center">
                  <div className="text-5xl font-light text-gray-800 mb-2" data-testid="timer-display">
                    {formatTime(timeLeft)}
                  </div>
                  {timerComplete && (
                    <div className="flex items-center justify-center gap-2 text-green-600 animate-pulse">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Time complete</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-end">
            {!isLastStep ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-8 py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                data-testid="next-step-btn"
              >
                <span className="font-medium">Next Step</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={resetExercise}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white px-8 py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                data-testid="complete-exercise-btn"
              >
                <Check className="w-5 h-5" />
                <span className="font-medium">Complete Exercise</span>
              </button>
            )}
          </div>
        </div>

        {/* Roles Reference */}
        <div className="mt-8 bg-white/90 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 text-center">Roles for this exercise:</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Initiator - Left side with rose/red theme */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg p-4 border-2 border-rose-300">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                <span className="font-bold text-rose-700 text-lg">🎯 INITIATOR</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">{selectedExercise.roles.initiator}</p>
            </div>
            {/* Inquirer - Right side with purple theme */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border-2 border-purple-300">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="font-bold text-purple-700 text-lg">🎧 INQUIRER</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">{selectedExercise.roles.inquirer}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouplesExercises;
